/**
 * Integer arithmetic for money. Every amount is integer paise and every rate is
 * integer basis points (500 = 5%). Nothing in billing-core uses floating point:
 * products and quotients go through BigInt, so they are exact at any size, and
 * the one place that rounds is `mulDivRound`.
 */

/**
 * round(a × b ÷ c), rounding half away from zero: 2.5 → 3, −2.5 → −3, 2.4 → 2.
 * This is the only rounding rule in billing-core.
 */
export function mulDivRound(a: number, b: number, c: number): number {
	assertInteger(a, "a");
	assertInteger(b, "b");
	assertInteger(c, "c");
	if (c === 0) throw new RangeError("Division by zero");
	const n = BigInt(a) * BigInt(b);
	const d = BigInt(c);
	let q = n / d; // BigInt division truncates toward zero
	const r = n % d;
	const absR = r < 0n ? -r : r;
	const absD = d < 0n ? -d : d;
	if (2n * absR >= absD) q += n < 0n === d < 0n ? 1n : -1n;
	return toSafeNumber(q);
}

/**
 * floor(a × b ÷ c) and the remainder, for non-negative integers. Used to spread
 * an amount in proportion (largest remainder method).
 */
export function mulDivFloor(
	a: number,
	b: number,
	c: number,
): { quotient: number; remainder: bigint } {
	assertInteger(a, "a");
	assertInteger(b, "b");
	assertInteger(c, "c");
	if (a < 0 || b < 0 || c <= 0) throw new RangeError("mulDivFloor needs a, b ≥ 0 and c > 0");
	const n = BigInt(a) * BigInt(b);
	const d = BigInt(c);
	return { quotient: toSafeNumber(n / d), remainder: n % d };
}

export function assertInteger(value: number, name: string): void {
	if (!Number.isSafeInteger(value))
		throw new RangeError(`${name} must be a safe integer, got ${value}`);
}

/** A non-negative integer amount of paise. */
export function assertPaise(value: number, name: string): void {
	assertInteger(value, name);
	if (value < 0) throw new RangeError(`${name} must not be negative, got ${value}`);
}

/** Basis points between 0 and 10000 (0% to 100%). */
export function assertBasisPoints(value: number, name: string): void {
	assertInteger(value, name);
	if (value < 0 || value > 10000) {
		throw new RangeError(`${name} must be between 0 and 10000 basis points, got ${value}`);
	}
}

export function sum(values: readonly number[]): number {
	let total = 0;
	for (const v of values) total += v;
	assertInteger(total, "sum");
	return total;
}

function toSafeNumber(value: bigint): number {
	const n = Number(value);
	if (!Number.isSafeInteger(n)) throw new RangeError(`Amount out of range: ${value}`);
	return n;
}
